/** @format */
import { Ref } from 'vue';
import { useDebounceFn as useDebounce } from '@vueuse/core';
import { ElMessage as $message } from 'element-plus';
import { request as requestFunc, useGlobalStoreWithOut } from '@power/runtime-vue3';
import { flat } from './util';
import { getWidgetItem } from './transWidgetDatas';
import type { FieldsType, ScriptFuncType } from '../types';

// 不需要提到公共utils文件，这里只对字段防抖使用，可以统一调整delay值
export const useDebounceFn = (fn, delay = 500) => useDebounce(fn, delay);

export const getFormFunc = (
	formDatas: Record<string, any>,
	widgetDatas: FieldsType[],
	searchSelect?: string,
): ScriptFuncType => {
	const globalStore = useGlobalStoreWithOut();
	const { userInfo } = storeToRefs(globalStore);

	// 1. 获取值
	const getValueFunc = (key?: string) => {
		return key ? formDatas[key] : formDatas;
	};

	// 2. 修改值  setValue(key:string, value:any)
	const setValueFunc = (key: string, value: any = '') => {
		if (key) {
			formDatas[key] = value;
		}
	};

	// 3. 获取字段属性 getPcProps(key:string, code?:string)
	const getPcPropsFunc = (key: string, code?: string) => {
		const flatArr = flat<FieldsType[]>(widgetDatas);
		const item = flatArr.find((itm) => itm.pcProps.code === key);

		return item && code ? item.pcProps[code] : item?.pcProps;
	};

	// 4. 修改字段属性 setpcProps(code:string, code:string,value:any)
	const setPcPropsFunc = (code: string, key: string, value: any) => {
		if (code && key) {
			const item = getWidgetItem(widgetDatas, (itm) => itm.pcProps.code === code);
			if (JSON.stringify(item) !== '{}') {
				item.pcProps[key] = value;
			}
		}
	};

	// 5. 获取用户信息 getUser(key?:string)
	const getUserFunc = (key?: string) => {
		return key ? userInfo.value.user[key] : userInfo.value.user;
	};

	const getSearchSelect = () => {
		return searchSelect ? searchSelect : '';
	};

	return {
		form: {
			getValue: {
				acProps: { meta: '全局方法-获取数据', value: 'form.getValue(key)' },
				script: getValueFunc,
			},
			setValue: {
				acProps: { meta: '全局方法-设置数据', value: 'form.setValue(key,value)' },
				script: setValueFunc,
			},
			getPcProps: {
				acProps: { meta: '全局方法-获取属性', value: 'form.getPcProps(code,key)' },
				script: getPcPropsFunc,
			},
			setPcProps: {
				acProps: { meta: '全局方法-设置属性', value: 'form.setPcProps(code,key,value)' },
				script: setPcPropsFunc,
			},
			getSearchSelect: {
				acProps: { meta: '获取搜索条件', value: 'form.getSearchSelect()' },
				script: getSearchSelect,
			},
			request: {
				acProps: { meta: '数据请求', value: `form.request({url:'',method:'get',})` },
				script: requestFunc,
			},
			getUser: {
				acProps: { meta: '获取用户信息', value: 'form.getUser(key)' },
				script: getUserFunc,
			},
			getAllList: {
				acProps: { meta: '获取列表数据', value: 'form.getAllList(key)' },
				script: (key) => getPcPropsFunc(key, 'getAllList')(),
			},
			getCheckedList: {
				acProps: { meta: '获取列表选中数据', value: 'form.getCheckedList(key)' },
				script: (key) => getPcPropsFunc(key, 'getCheckedList')(),
			},
			updateListItem: {
				acProps: { meta: '更新列表数据', value: 'form.updateListItem(key, formData)' },
				script: (key, formData) => getPcPropsFunc(key, 'updateListItem')(formData),
			},
			delListItem: {
				acProps: { meta: '删除列表数据', value: 'form.delListItem(key, rowKey)' },
				script: (key, rowKey) => getPcPropsFunc(key, 'delListItem')(rowKey),
			},
			hiddenSubTableColumn: {
				acProps: { meta: '隐藏列', value: 'form.hiddenSubTableColumn(key, colCode)' },
				script: (key, colCode) => getPcPropsFunc(key, 'hiddenSubTableColumn')(colCode),
			},
			showSubTableColumn: {
				acProps: { meta: '显示列', value: 'form.showSubTableColumn(key, colCode)' },
				script: (key, colCode) => getPcPropsFunc(key, 'showSubTableColumn')(colCode),
			},
			disabledSubTableCol: {
				acProps: { meta: '只读列', value: 'form.disabledSubTableCol(key, col, row)' },
				script: (key, col, row) => getPcPropsFunc(key, 'disabledSubTableCol')(col, row),
			},
			enabledSubTableCol: {
				acProps: { meta: '可用列', value: 'form.enabledSubTableCol(key, col, row)' },
				script: (key, col, row) => getPcPropsFunc(key, 'enabledSubTableCol')(col, row),
			},
		},
		callback: {},
	};
};

export const transGetScript = (obj: ScriptFuncType): Record<string, Record<string, Function>> => {
	return Object.keys(obj).reduce((ob, it) => {
		ob[it] = Object.keys(obj[it]).reduce((o, i) => {
			o[i] = obj[it][i].script;
			return o;
		}, {});
		return ob;
	}, {});
};

class Script {
	constructor(formDatas: Ref<Record<string, any>>, widgetDatas: Ref<FieldsType[]>, otherFunc?: ScriptFuncType) {
		this.formDatas = formDatas;
		this.widgetDatas = widgetDatas;
		otherFunc && (this.otherFunc = otherFunc);
	}
	private scriptList = {};
	private formDatas: Ref<Record<string, any>> = ref({}); // 表单数据
	private widgetDatas: Ref<FieldsType[]> = ref([]); // 结构数据
	private otherFunc: ScriptFuncType = {}; // 其他方法，用以扩容
	private searchSelect = '';

	// 获取脚本自动填充数据
	getScriptSnippets() {
		const scripts = this.getFunc();
		const snippets = Object.keys(scripts).reduce((arr: any[], key) => {
			const list = Object.keys(scripts[key]).reduce((ar: any[], ke) => {
				if (scripts[key][ke].acProps) {
					ar.push({
						caption: ke,
						score: 1,
						...scripts[key][ke].acProps,
						meta: `${key}--${scripts[key][ke].acProps?.meta}`,
					});
				}
				return ar;
			}, []);
			return arr.concat(list);
		}, []);

		return snippets;
	}

	// 导出所有脚本
	getFunc() {
		const otherFuncList =
			typeof this.otherFunc === 'function'
				? this.otherFunc(getFormFunc(this.formDatas.value, this.widgetDatas.value, this.searchSelect))
				: this.otherFunc;

		return { ...getFormFunc(this.formDatas.value, this.widgetDatas.value, this.searchSelect), ...otherFuncList };
	}

	// 获取脚本入参
	getFuncInput() {
		return transGetScript(this.getFunc());
	}

	// 字符串转方法
	transStrToFunc(script: string) {
		const input = Object.keys(this.getFuncInput()).join(',');
		const AsyncFunction = Object.getPrototypeOf(async function () {}).constructor;
		return new AsyncFunction(`{${input}}`, script);
	}

	// 校验脚本格式
	pickEvalStr(script: string) {
		try {
			this.transStrToFunc(script);
			return true;
		} catch (e) {
			$message.error('脚本格式化失败');
			//console.log(e);
			return false;
		}
	}

	// 缓存脚本
	private setScript(code: string, type: string, script: string) {
		if (!this.scriptList[code]) {
			this.scriptList[code] = {};
		}
		if (!this.scriptList[code][type]) {
			if (this.pickEvalStr(script)) {
				const func = this.transStrToFunc(script);
				this.scriptList[code][type] = func;
			}
		}
	}

	// 执行脚本
	runScript(code: string, type: string, script?: string, searchSelect?: string) {
		this.searchSelect = searchSelect || '';
		const input = this.getFuncInput();
		if (script) {
			this.setScript(code, type, script);
		}
		return new Promise((resolve, reject) => {
			try {
				this.scriptList[code][type]({ ...input, callback: resolve });
			} catch (e) {
				$message.error('脚本执行报错');
				console.error(e);
				reject(e);
			}
		});
	}

	desctory() {
		this.scriptList = {};
		this.formDatas = ref({});
		this.widgetDatas = ref([]);
		this.otherFunc = {};
	}
}

export default Script;
